import { Component, Mixins, Watch } from 'vue-property-decorator';
import { namespace } from 'vuex-class';

import '@gig/magic-ui-kit';
import '@gig/magic-pages';
import '@gig/magic-responsible-gaming-prompters';
import { magic } from '@gig/magic-sdk';
import { notificationService } from '@gig/magic-messaging';
import type { IMessageEmit } from '@gig/magic-messaging';
import type { IDepositDataEvent } from '@gig/magic-sdk/dist/events';
import { SessionCode } from '@gig/magic-sdk/dist/events';
import {
    CssDevice,
    FeatureEnabledV2,
    getTrackingService,
    ipCountryQuery,
    MagicConfigMixin,
    nativeAppService,
    queryTranslations,
    SessionChange,
} from '@gig/magic-common';
import '@gig/magic-gamification/dist/components/loyalty-progress';

import { MainNavigation } from '../main-navigation';
import { TopNavigation } from '../top-navigation';
import { BottomNavigation } from '../bottom-navigation';
import { AppFooter } from '../footer';
import { LiveChat } from '../live-chat';
import { ModalContainer } from '../modal-container';
import { RegilyManager } from '../regily-manager';
import { TermsUpdatedManager } from '../terms-updated-manager';
import { SourceOfWealthManager } from '../source-of-wealth-manager';
import { GameFrame } from '../game-frame';
import { ContinuePlaying } from '../continue-playing';
import { BlockManager } from '../block-manager';
import { ZendeskLoader } from '../zendesk-loader';
import { NotFound } from '../not-found';

import type { OmitFirst } from '../../types';
import { AuthRestriction, config, liveChat, zendeskChat } from '../../services';
import { RETURNING_USER_KEY } from '../../util';

import { RouteName } from '../../routes/routes.constants';
import { prerender } from '../../services/seo/prerender.service';
import {
    actions as rgActions,
    RESPONSIBLE_GAMING_NAMESPACE,
} from '../../store/modules/responsible-gaming';

import '../../styles/default.scss';
import { JurisdictionMixin } from '../../mixins/jurisdiction.mixin';
import { NotFoundMixin } from '../../mixins/notfound.mixin';
import {
    BREAKPOINT_DEVICE_UPDATED,
    BREAKPOINT_FULLSCREEN_UPDATED,
    DEVICE_QUERY_IS_IOS,
    DEVICE_QUERY_IS_IPAD,
    DEVICE_QUERY_IS_MOBILE,
    ENABLE_CONTINUE_PLAYING_UPDATED,
    Feature,
    GAMES_CURRENT_GAME_SLUG_UPDATED,
    GAMES_JACKPOTS_LIST,
    GAMES_LIST,
    MESSAGING_MARK_READ,
    NET_POSITION_BAR_UPDATED,
    PANIC_BAR_VISIBLE,
    RECONNECTING_UPDATED,
} from '@gig/magic-messages';
import { Country } from '@gig/magic-sdk/dist/adapter/users/getCountries';
import { alternateLink } from '@gig/magic-frontend-toolkit/dist/services/alternate-link';
import { linktag } from '@gig/magic-frontend-toolkit/dist/services/linktag';

const rgModule = namespace(RESPONSIBLE_GAMING_NAMESPACE);

@Component({
    components: {
        MainNavigation,
        TopNavigation,
        BottomNavigation,
        AppFooter,
        LiveChat,
        ModalContainer,
        TermsUpdatedManager,
        SourceOfWealthManager,
        GameFrame,
        ContinuePlaying,
        BlockManager,
        RegilyManager,
        ZendeskLoader,
        NotFound,
    },
})
export default class App extends Mixins(
    MagicConfigMixin,
    JurisdictionMixin,
    NotFoundMixin,
) {
    @FeatureEnabledV2({
        slug: Feature.COOKIE_POPUP,
    })
    cookiePopupEnabled = false;

    /**
     * is top navigation enabled
     */
    @FeatureEnabledV2({
        slug: Feature.TOP_NAVIGATION,
    })
    isTopNavigationEnabled = true;

    country: Country | null = null;

    isLoggedIn = false;

    reconnecting: boolean = false;

    @rgModule.Action('clearLimits')
    clearLimits!: OmitFirst<typeof rgActions.clearLimits>;

    showNetPositionBar = false;
    enableContinuePlaying = false;
    isMobile = false;
    isFullscreen: boolean = false;
    shouldShowPWAInstall: boolean = false;
    currentGameSlug: string | null = null;
    panicBarVisible = false;
    isWalletOpen: boolean = false;

    private subscriptions: Array<() => void> = [];

    get isMobileGamePlayView() {
        return !!this.currentGameSlug && this.isMobile;
    }

    get shouldDisableRGAlerts() {
        return this.$route.matched.some((route) => {
            return route.meta.shouldDisableRGAlerts === true;
        });
    }

    get shouldHideMainMenu() {
        const isLandingPage = this.$route.name === RouteName.LANDING;

        const hasHideMainMenuMeta = this.$route.matched.some((route) => {
            return route.meta.shouldHideMainMenu === true;
        });

        return hasHideMainMenuMeta || (!this.isMobile && isLandingPage);
    }

    get isNotFoundRoute(): boolean {
        return this.isNotFound;
    }

    get isSportsbookPage(): boolean {
        return this.$route.name === RouteName.SPORTSBOOK;
    }

    mounted() {
        const attributes = config.cookieBot;
        if (!attributes?.src) {
            return;
        }

        const cookieBot = document.createElement('script');
        for (const [key, value] of Object.entries(attributes)) {
            cookieBot.setAttribute(key, value);
        }
        document.head.appendChild(cookieBot);
    }

    async created() {
        this.country = await ipCountryQuery();

        if (!window.inTestEnvironment) {
            prerender.waitFor(this.requestGames(), this.requestJackpots());
        }

        this.setDefaultMetaTags();
        linktag.setLanguagesConfig(config.languages);

        if (magic.session.isCreated()) {
            this.handleSessionCreated();
        }
        magic.events.sessionDestroyed(() => this.handleSessionDestroyed());
        magic.events.sessionCreated(() => this.handleSessionCreated());
        magic.events.sessionChange(this.handleSessionChanged);
        magic.events.firstDeposit(this.handleFirstDeposit);
        magic.events.subsequentDeposit(this.handleSubsequentDeposit);

        liveChat?.updateLiveChat(magic.session.getUser());

        this.checkIsWalletOpen();

        this.getPWAstatus();

        window.eventBus.subscribe<boolean>(
            RECONNECTING_UPDATED,
            (e) => {
                this.reconnecting = e.payload!;
            },
            { replay: true },
        );

        window.eventBus.subscribe<boolean>(
            NET_POSITION_BAR_UPDATED,
            (event) => {
                this.showNetPositionBar = event.payload!;
            },
            { replay: true },
        );

        window.eventBus.subscribe<boolean>(
            ENABLE_CONTINUE_PLAYING_UPDATED,
            (event) => {
                this.enableContinuePlaying = event.payload!;
            },
            { replay: true },
        );

        window.eventBus.subscribe<CssDevice>(
            BREAKPOINT_DEVICE_UPDATED,
            async () => {
                this.isMobile = await window.queryBus.query(
                    DEVICE_QUERY_IS_MOBILE,
                )!;
            },
            { replay: true },
        );

        window.eventBus.subscribe<boolean>(
            BREAKPOINT_FULLSCREEN_UPDATED,
            (e) => {
                this.isFullscreen = e.payload!;
            },
            { replay: true },
        );

        window.eventBus.subscribe<string | null>(
            GAMES_CURRENT_GAME_SLUG_UPDATED,
            (e) => {
                this.currentGameSlug = e.payload!;
            },
            { replay: true },
        );

        window.eventBus.subscribe<boolean>(
            PANIC_BAR_VISIBLE,
            async (e) => {
                this.panicBarVisible = e.payload!;
            },
            { replay: true },
        );
    }

    @Watch('magicConfig.language')
    async languageChange() {
        if (config?.zendesk?.version === 'legacy') {
            zendeskChat.setZendeskSettings();
        }
    }

    private requestJackpots(): Promise<void> {
        return new Promise((resolve) => {
            const subscription = window.eventBus.subscribe(
                GAMES_JACKPOTS_LIST,
                () => {
                    resolve();
                    subscription.unsubscribe();
                },
                { replay: true },
            );
        });
    }

    @Watch('$route.path')
    onRouteChange() {
        this.setDefaultMetaTags();
        this.resetNotFound();
    }

    get isTopNavigationLight() {
        return this.$route.matched.reduce(
            (prev, cur) => ({ ...prev, ...cur.meta.topNavigation }),
            {
                isLight: false,
            },
        ).isLight;
    }

    private setDefaultMetaTags() {
        queryTranslations().then(() => {
            linktag.setCanonicalTagFromPath(this.$route.path);
            alternateLink.setAlternateTag(this.$route.path, {
                useBaseUrl: true,
            });
        });
    }

    @Watch('$route.query')
    checkIsWalletOpen() {
        const queryModalPath = this.$route.query.modal;
        this.isWalletOpen =
            (Array.isArray(queryModalPath) &&
                queryModalPath.some((path) =>
                    path?.includes(RouteName.WALLET),
                )) ||
            !!queryModalPath?.includes(RouteName.WALLET);
        this.resetNotFound();
    }

    @SessionChange({ immediate: true })
    async onSessionChange(isUserLoggedIn: boolean) {
        this.isLoggedIn = isUserLoggedIn;
    }

    onAcceptedCookiePopup() {
        getTrackingService().trackCookieAcceptance();
    }

    markMessageAsRead(payload: IMessageEmit) {
        window.commandBus.publish(MESSAGING_MARK_READ, payload);
    }

    async getPWAstatus() {
        const [isIOS, isIpad] = await Promise.all([
            window.queryBus.query<boolean>(DEVICE_QUERY_IS_IOS),
            window.queryBus.query<boolean>(DEVICE_QUERY_IS_IPAD),
        ]);
        this.shouldShowPWAInstall = isIOS! || isIpad!;
    }

    private requestGames(): Promise<void> {
        return new Promise((resolve) => {
            const subscription = window.eventBus.subscribe(
                GAMES_LIST,
                () => {
                    resolve();
                    subscription.unsubscribe();
                },
                { replay: true },
            );
        });
    }

    private async handleSessionDestroyed() {
        liveChat?.updateLiveChat(null);
        this.clearLimits();

        this.subscriptions.forEach((unsub) => unsub());
        this.subscriptions = [];

        this.checkRouteRestriction();
    }

    private handleSessionChanged(code: SessionCode) {
        if (
            [
                SessionCode.EXPIRED,
                SessionCode.TERMINATED_BY_ANOTHER_LOGIN,
                SessionCode.TERMINATED_BY_PRESET_TIME,
                SessionCode.TERMINATED_BY_SELF_EXCLUSION,
                SessionCode.TERMINATED_BY_GEO_LOCATION,
            ].includes(code)
        ) {
            nativeAppService.postSystemLogout();
        }

        if (code === SessionCode.TERMINATED_BY_GEO_LOCATION) {
            notificationService.error(this.$t('session.logout.geo-location'));
        }

        if (SessionCode.LOGGED_OFF) {
            nativeAppService.postLogout();
        }
    }

    private handleFirstDeposit(event: IDepositDataEvent) {
        const { amount, currency } = event;
        nativeAppService.trackFirstDeposit(amount, currency);
    }

    private handleSubsequentDeposit(event: IDepositDataEvent) {
        const { amount, currency } = event;
        nativeAppService.trackDeposit(amount, currency);
    }

    private checkRouteRestriction() {
        const lastRestriction = this.$route.matched.reduce(
            (prev, { meta }) =>
                meta.authRestriction !== undefined
                    ? meta.authRestriction
                    : prev,
            AuthRestriction.UNRESTRICTED,
        );
        if (lastRestriction === AuthRestriction.AUTH_REQUIRED) {
            if (this.isAGCO || this.isLOTBA) {
                // both lotba and agco display the session resume modal when user logs out,
                // on standard flow the user is redirected to login screen which covers the
                // session modal. We ensure therefore the user is redirected to a non auth route (HOME)
                // so the login modal is not displayed and the session resume modal can be displayed
                this.$router.push({
                    name: RouteName.HOME,
                });
            } else {
                this.$router.replace({
                    name: RouteName.LOGIN,
                    query: {
                        backTo: this.$route.fullPath,
                    },
                });
            }
        }
    }

    private async handleSessionCreated() {
        const user = magic.session.getUser();

        liveChat?.updateLiveChat(user);

        this.subscriptions.push(
            magic.events.sowqRequested(() => {
                if (this.$route.name !== RouteName.SOW) {
                    this.$router.push({
                        name: RouteName.SOW,
                    });
                }
            }).unsub,
        );

        try {
            window.localStorage.setItem(RETURNING_USER_KEY, '1');
        } catch (e) {
            console.error(e);
        }
    }
}
